import { Fragment, useState } from "react";
import { Download, Flame, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, money, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { useAuth } from "@/lib/auth";
import { useTerms } from "@/lib/industry";

function NoteBox({ lead, onAdded }) {
  const [t, setT] = useState("");
  const add = async (e) => {
    e.preventDefault();
    if (!t.trim()) return;
    try { await api.post(`/admin/leads/${lead.id}/notes`, { text: t }); setT(""); onAdded(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="mt-3" data-testid={`lead-notes-${lead.id}`}>
      {(lead.notes || []).map((n) => (
        <p key={n.id} className="text-sm border-l-2 border-ba pl-3 mb-2"><span className="text-xs text-slate-400">{n.author} · {format(new Date(n.created_at), "d MMM, HH:mm")}</span><br />{n.text}</p>
      ))}
      <form onSubmit={add} className="flex gap-2 mt-2">
        <input value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note (call outcome, next step…)" className="flex-1 h-9 rounded-full border px-4 text-sm bg-white" data-testid={`lead-note-input-${lead.id}`} />
        <AdminButton type="submit" data-testid={`lead-note-add-${lead.id}`}>Add note</AdminButton>
      </form>
    </div>
  );
}

const STATUSES = [["new", "New"], ["contacted", "Contacted"], ["viewing_booked", "Viewing booked"], ["reserved", "Reserved"]];
const KIND = { viewing: "Viewing", interest: "Interest", quote: "Saved home", design: "Design studio", click: "Booking click" };
const today = () => new Date().toISOString().slice(0, 10);
const TIER = { hot: ["Hot", "bg-rose-100 text-rose-700 border-rose-200"], warm: ["Warm", "bg-amber-100 text-amber-800 border-amber-200"], cold: ["Cold", "bg-sky-50 text-sky-700 border-sky-200"] };
const ScoreBadge = ({ l }) => {
  const [lab, cls] = TIER[l.tier] || TIER.cold;
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap ${cls}`} title={(l.reasons || []).join(" · ")} data-testid={`lead-score-${l.id}`}>{l.tier === "hot" && <Flame className="w-3 h-3" />}{lab} · {l.score ?? 0}</span>;
};

export default function AdminLeads() {
  const [leads, reload] = useList("/admin/leads");
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const [sort, setSort] = useState("hot");
  const [team] = useList("/admin/team");
  const { user } = useAuth();
  const { t, sells } = useTerms();
  if (!leads) return null;
  const statuses = sells ? STATUSES : STATUSES.map(([k, l]) => [k, k === "viewing_booked" ? (t.id === "fitness" ? "Session booked" : "Visit booked") : k === "reserved" ? "Closed" : l]);
  const patch = async (l, body) => {
    try { await api.patch(`/admin/leads/${l.id}`, body); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const exportCsv = async () => {
    const { data } = await api.get("/admin/leads/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = "leads.csv";
    a.click();
  };
  const due = (l) => l.follow_up_at && l.follow_up_at <= today();
  const cancel = async (l) => {
    if (!window.confirm(`Cancel ${l.name}'s booking on ${l.preferred_at}? The time slot opens up again.`)) return;
    try { await api.post(`/admin/leads/${l.id}/cancel-booking`); toast.success("Booking cancelled"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`Delete the lead for ${l.name}? Any booked time is freed up. This can't be undone.`)) return;
    try { await api.delete(`/admin/leads/${l.id}`); toast.success("Lead deleted"); setOpenId(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = [...leads].sort((a, b) => (sort === "hot" ? (b.score || 0) - (a.score || 0) : 0)).filter((l) => filter === "all" || (filter === "mine" ? l.assigned_to === user.id : filter === "due" ? due(l) : l.status === filter));
  return (
    <div>
      <PageTitle help="leads" title={sells ? "Leads" : "Enquiries"} sub={`${leads.length} total`} right={<AdminButton variant="ghost" onClick={exportCsv} data-testid="leads-export-csv"><Download className="w-4 h-4" />Export CSV</AdminButton>} />
      <div className="flex gap-2 mb-4 flex-wrap">
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="h-9 rounded-full border px-3 text-sm bg-white" data-testid="leads-sort"><option value="hot">Hottest first</option><option value="new">Newest first</option></select>
        {[["all", "All"], ["mine", "Assigned to me"], ["due", `Follow-up due (${leads.filter(due).length})`], ...statuses].map(([k, l]) => <button key={k} onClick={() => setFilter(k)} className={`h-9 px-4 rounded-full text-sm border ${filter === k ? "bg-slate-900 text-white" : "bg-white"}`} data-testid={`leads-filter-${k}`}>{l}</button>)}
      </div>
      <div className="rounded-3xl bg-white border overflow-x-auto">
        <table className="w-full text-sm" data-testid="leads-table">
          <thead><tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b"><th className="p-4">Received</th><th>Score</th><th>{t.person}</th><th>Type</th><th>Interested in</th><th>Preferred</th><th>Owner</th><th>Follow-up</th><th className="pr-4">Status</th></tr></thead>
          <tbody>
            {shown.map((l) => (
              <Fragment key={l.id}>
                <tr className="border-b hover:bg-slate-50 cursor-pointer" onClick={() => setOpenId(openId === l.id ? null : l.id)} data-testid={`lead-row-${l.id}`}>
                  <td className="p-4 whitespace-nowrap text-slate-500">{format(new Date(l.created_at), "d MMM, HH:mm")}</td>
                  <td className="pr-3"><ScoreBadge l={l} /></td>
                  <td><p className="font-medium">{l.name}</p><p className="text-xs text-slate-500">{l.email}{l.phone ? ` · ${l.phone}` : ""}</p></td>
                  <td>{KIND[l.kind] || l.kind}</td>
                  <td className="text-slate-600">{[l.development_name, l.plot_number && `Lot ${l.plot_number}`, l.house_type_name].filter(Boolean).join(" · ") || "—"}</td>
                  <td className="text-slate-600 whitespace-nowrap">{l.preferred_at || "—"}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <select value={l.assigned_to || ""} onChange={(e) => patch(l, { assigned_to: e.target.value })} className="h-9 rounded-full border px-3 text-sm bg-white max-w-[140px]" data-testid={`lead-owner-${l.id}`}>
                      <option value="">Unassigned</option>
                      {(team || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                    </select>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="date" value={l.follow_up_at || ""} onChange={(e) => patch(l, { follow_up_at: e.target.value })} className={`h-9 rounded-full border px-3 text-sm ${due(l) ? "border-rose-400 bg-rose-50 text-rose-700" : "bg-white"}`} data-testid={`lead-followup-${l.id}`} />
                  </td>
                  <td className="pr-4" onClick={(e) => e.stopPropagation()}>
                    <select value={l.status} onChange={(e) => patch(l, { status: e.target.value })} className="h-9 rounded-full border px-3 text-sm bg-white" data-testid={`lead-status-${l.id}`}>
                      {statuses.map(([k, lab]) => <option key={k} value={k}>{lab}</option>)}
                    </select>
                  </td>
                </tr>
                {openId === l.id && (
                  <tr className="bg-slate-50 border-b" data-testid={`lead-detail-${l.id}`}>
                    <td colSpan={9} className="p-4 text-sm">
                      <p className="mb-2" data-testid={`lead-score-reasons-${l.id}`}><span className="text-slate-500">Why {TIER[l.tier]?.[0].toLowerCase() || "cold"}:</span> {(l.reasons || []).join(" · ") || "No tour activity yet"}</p>
                      {l.booking && <p className="mb-2 flex items-center gap-3"><span><span className="text-slate-500">Booked:</span> <strong>{l.preferred_at}</strong></span><button onClick={() => cancel(l)} className="text-rose-600 text-xs font-semibold underline" data-testid={`lead-cancel-booking-${l.id}`}>Cancel booking</button></p>}
                      {l.message && <p className="mb-2"><span className="text-slate-500">Message:</span> {l.message}</p>}
                      {l.selections?.length > 0 && <p><span className="text-slate-500">Selections:</span> {l.selections.map((s) => `${s.category}: ${s.option}`).join(" · ")} — <strong>{money(l.total)}</strong></p>}
                      {l.selection_id && <a href={`/home/${l.selection_id}`} target="_blank" rel="noreferrer" className="text-bp font-semibold">Open saved home</a>}
                      <NoteBox lead={l} onAdded={reload} />
                      <button onClick={() => remove(l)} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:underline" data-testid={`lead-delete-${l.id}`}><Trash2 className="w-3.5 h-3.5" />Delete lead</button>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {shown.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-slate-400">No leads yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
